import React, { useEffect } from "react";
import { Button, Grid, Stack, MenuItem } from "@mui/material";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { StandardCard } from "../../../components/Cards";
import { PageContainer } from "../../../components/container/PageContainer";
import { CustomFormLabel } from "../../../components/formsComponents";
import { ExpandableTextField } from "../../../components/formsComponents/CustomTextField";
import { PageLoader } from "../../../components/PageLoader/PageLoader";
import { TextFieldWithLabel } from "../../../components/TextFieldWithLabel/TextFieldWithLabel";
import { LoadState } from "../../../constants/enums";
import { useDispatchWrapper } from "../../../hooks";
import { Breadcrumb } from "../../../layout/fullLayout/breadcrumb/Breadcrumb";
import { useBillingCompanyFromStorage } from "../../../providers";
import { IStoreState } from "../../../redux/initialStoreState";
import {
  fetchCallCampaignDataAsync,
  upsertCallCampaignDataAsync,
  clearCallCampaignDataState,
} from "../../../redux/calling/calling.actions";
import { ICallCampaignData } from "../../../redux/calling/calling.types";
import { CALLING_ROUTE } from "../calling.constant";

export const QuickCampaignData: React.FC = () => {
  const { uuid } = useParams() as { uuid: string };
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();
  const { company_outlet_uuid } = useBillingCompanyFromStorage();

  const { data: leadData, loading } = useSelector(
    (state: IStoreState) => state.calling.callCampaignData.data
  );

  const formik = useFormik<ICallCampaignData>({
    initialValues: {
      customer_name: "",
      contact_number: "",
      vehicle_number: "",
      registration_year: "",
      secondary_contact_number: "",
      email: "",
      city: "",
      state: "",
      address: "",
      pin_code: "",
      status: "ACTIVE",
      comment: "",
      company_outlet_uuid: company_outlet_uuid || "",
    } as any,

    onSubmit: (values) => {
      const submissionValues: any = { ...values };

     
      if (
        !submissionValues.registration_year ||
        submissionValues.registration_year === "Invalid date"
      ) {
        delete submissionValues.registration_year;
      }

      dispatch(
        upsertCallCampaignDataAsync(submissionValues, (success) => {
          if (success) navigate("/calling");
        })
      );
    },
  });

  useEffect(() => {
    if (uuid) dispatch(fetchCallCampaignDataAsync(uuid));
  }, [uuid, dispatch]);

  useEffect(() => {
    if (leadData) {
      formik.setValues({
        ...formik.values,
        ...leadData,
      });
    }
  }, [leadData]);

  useEffect(() => {
    return () => {
      dispatch(clearCallCampaignDataState());
    };
  }, [dispatch]);

  return (
    <PageContainer
      title={uuid ? "Edit Lead" : "Create Lead"}
      description="inner"
    >
      <PageLoader loading={loading === LoadState.InProgress}>
        <Breadcrumb
          title="Manage Campaign Data"
          heading="Manage Campaign Data"
          items={[
            { title: "Campaign", to: CALLING_ROUTE },
            { title: "Campaign Data", to: `${CALLING_ROUTE}/campaign-data` },
            { title: "Quick Data" },
          ]}
        />


        <StandardCard>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextFieldWithLabel
                  heading="Customer Name"
                  name="customer_name"
                  value={formik.values.customer_name || ""}
                  onChange={formik.handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextFieldWithLabel
                  heading="Contact Number"
                  name="contact_number"
                  value={formik.values.contact_number || ""}
                  onChange={formik.handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextFieldWithLabel
                  heading="Secondary Contact Number"
                  name="secondary_contact_number"
                  value={formik.values.secondary_contact_number || ""}
                  onChange={formik.handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextFieldWithLabel
                  heading="Email"
                  name="email"
                  value={formik.values.email || ""}
                  onChange={formik.handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextFieldWithLabel
                  heading="City"
                  name="city"
                  value={formik.values.city || ""}
                  onChange={formik.handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextFieldWithLabel
                  heading="State"
                  name="state"
                  value={formik.values.state || ""}
                  onChange={formik.handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextFieldWithLabel
                  heading="Address"
                  name="address"
                  value={formik.values.address || ""}
                  onChange={formik.handleChange}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextFieldWithLabel
                  heading="Pin Code"
                  name="pin_code"
                  value={formik.values.pin_code || ""}
                  onChange={formik.handleChange}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Stack mt={3} direction="row" justifyContent="flex-end" spacing={2}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button variant="contained" type="submit">
                Save Record
              </Button>
            </Stack>
          </form>
        </StandardCard>
      </PageLoader>
    </PageContainer>
  );
};
